import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Activity, Loader2, AlertTriangle, Info,
  Check, X, Utensils, ThumbsUp, ThumbsDown, Target, RefreshCw,
  User, Users
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { TIER_NAMES } from '@/branding';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SugarHomeostasisProgramPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('daily-sugar');
  const [loading, setLoading] = useState(true);
  const [sugarData, setSugarData] = useState(null);

  const fetchSugarAnalysis = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/myinsul/sugar-homeostasis/analysis`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSugarData(response.data);
    } catch (error) {
      toast.error('Failed to load sugar analysis');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSugarAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const analysis = sugarData?.analysis;
  const limits = sugarData?.limits;

  const getStatusColor = (status) => {
    if (status === 'within_limit') return 'text-emerald-600';
    if (status === 'borderline') return 'text-amber-600';
    return 'text-red-600';
  };

  const getStatusBgColor = (status) => {
    if (status === 'within_limit') return 'bg-emerald-50 border-emerald-200';
    if (status === 'borderline') return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  const tabs = [
    { id: 'daily-sugar', label: 'Daily Sugar Consumption', icon: Target },
    { id: 'culprit-foods', label: 'My Culprit Foods', icon: ThumbsDown },
    { id: 'good-foods', label: 'My Good Foods', icon: ThumbsUp }
  ];

  // Pie chart colors
  const COLORS = {
    added: ['#ef4444', '#fecaca'],
    natural: ['#22c55e', '#bbf7d0'],
    men: ['#3b82f6', '#bfdbfe'],
    women: ['#ec4899', '#fbcfe8']
  };

  // Prepare pie chart data for men's limit
  const getMenLimitData = () => {
    if (!analysis?.daily_averages) return [];
    const value = analysis.daily_averages.estimated_added_sugar_g || 0;
    const limit = limits?.men_g || 36;
    return [
      { name: 'Consumed', value: Math.min(value, limit) },
      { name: 'Remaining', value: Math.max(0, limit - value) }
    ];
  };

  // Prepare pie chart data for women/children's limit
  const getWomenLimitData = () => {
    if (!analysis?.daily_averages) return [];
    const value = analysis.daily_averages.estimated_added_sugar_g || 0;
    const limit = limits?.women_children_g || 25;
    return [
      { name: 'Consumed', value: Math.min(value, limit) },
      { name: 'Remaining', value: Math.max(0, limit - value) }
    ];
  };

  // Prepare sugar type breakdown data
  const getSugarTypeData = () => {
    if (!analysis?.daily_averages) return [];
    return [
      { name: 'Added Sugar', value: analysis.daily_averages.estimated_added_sugar_g || 0 },
      { name: 'Natural Sugar', value: analysis.daily_averages.natural_sugar_g || 0 }
    ];
  };

  // Prepare sugar sources data for bar chart
  const getSugarSourcesData = () => {
    if (!analysis?.sugar_sources) return [];
    return [
      { name: 'Beverages', value: analysis.sugar_sources.beverages_percent || 0 },
      { name: 'Desserts', value: analysis.sugar_sources.desserts_sweets_percent || 0 },
      { name: 'Processed', value: analysis.sugar_sources.processed_foods_percent || 0 },
      { name: 'Fruits', value: analysis.sugar_sources.natural_fruits_percent || 0 },
      { name: 'Other', value: analysis.sugar_sources.other_percent || 0 }
    ];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/myinsul')}
                className="rounded-full"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center">
                  <Activity className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                    Sugar Homeostasis Program
                  </h1>
                  <p className="text-xs text-muted-foreground">Blood Sugar & Metabolic Health</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchSugarAnalysis}
                disabled={loading}
                className="rounded-full"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <span className="text-xs bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-medium">
                {TIER_NAMES.cdm}
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              data-testid={`${tab.id}-tab`}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              className={activeTab === tab.id 
                ? "rounded-full px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500" 
                : "rounded-full px-6 py-2"
              }
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : !sugarData?.has_data ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg p-8 text-center"
          >
            <AlertTriangle className="w-16 h-16 mx-auto text-purple-400 mb-4" />
            <h2 className="text-xl font-bold mb-2">No Data Available</h2>
            <p className="text-muted-foreground">{sugarData?.message || 'Start logging your meals to see sugar analysis!'}</p>
          </motion.div>
        ) : (
          <AnimatePresence mode="wait">
            {/* Daily Sugar Consumption Tab */}
            {activeTab === 'daily-sugar' && (
              <motion.div
                key="daily-sugar"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                {/* Date Range */}
                <div className="text-sm text-muted-foreground text-center">
                  Analysis period: {sugarData?.date_range?.start} to {sugarData?.date_range?.end} ({analysis?.days_analyzed || 0} days analyzed)
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-2xl shadow-lg p-6 border text-center">
                    <div className="text-4xl font-bold text-purple-600">
                      {analysis?.daily_averages?.total_sugar_g?.toFixed(1) || 0}g
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">Total Daily Sugar</div>
                  </div>
                  <div className="bg-white rounded-2xl shadow-lg p-6 border text-center">
                    <div className="text-4xl font-bold text-red-500">
                      {analysis?.daily_averages?.estimated_added_sugar_g?.toFixed(1) || 0}g
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">Added Sugar (Daily Avg)</div>
                  </div>
                  <div className="bg-white rounded-2xl shadow-lg p-6 border text-center">
                    <div className="text-4xl font-bold text-emerald-500">
                      {analysis?.daily_averages?.natural_sugar_g?.toFixed(1) || 0}g
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">Natural Sugar (Daily Avg)</div>
                  </div>
                </div>

                {/* Pie Charts - Gender Limits */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Men's Limit */}
                  <div className={`bg-white rounded-2xl shadow-lg p-6 border ${getStatusBgColor(analysis?.status_men)}`}>
                    <div className="flex items-center gap-2 mb-4">
                      <User className="w-5 h-5 text-blue-600" />
                      <h3 className="font-bold">Men's Daily Limit</h3>
                    </div>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={getMenLimitData()}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={70}
                            dataKey="value"
                            startAngle={90}
                            endAngle={-270}
                          >
                            {getMenLimitData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS.men[index]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${getStatusColor(analysis?.status_men)}`}>
                        {analysis?.daily_averages?.estimated_added_sugar_g?.toFixed(1) || 0}g / {limits?.men_g}g
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {analysis?.percentage_of_men_limit?.toFixed(0) || 0}% of daily limit
                      </div>
                      <div className={`text-sm font-medium mt-1 ${getStatusColor(analysis?.status_men)}`}>
                        {analysis?.status_men === 'within_limit' ? '✓ Within Limit' : 
                         analysis?.status_men === 'borderline' ? '⚠ Borderline' : '✗ Exceeds Limit'}
                      </div>
                    </div>
                  </div>

                  {/* Women/Children's Limit */}
                  <div className={`bg-white rounded-2xl shadow-lg p-6 border ${getStatusBgColor(analysis?.status_women_children)}`}>
                    <div className="flex items-center gap-2 mb-4">
                      <Users className="w-5 h-5 text-pink-600" />
                      <h3 className="font-bold">Women & Children's Daily Limit</h3>
                    </div>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={getWomenLimitData()}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={70}
                            dataKey="value"
                            startAngle={90}
                            endAngle={-270}
                          >
                            {getWomenLimitData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS.women[index]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${getStatusColor(analysis?.status_women_children)}`}>
                        {analysis?.daily_averages?.estimated_added_sugar_g?.toFixed(1) || 0}g / {limits?.women_children_g}g
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {analysis?.percentage_of_women_children_limit?.toFixed(0) || 0}% of daily limit
                      </div>
                      <div className={`text-sm font-medium mt-1 ${getStatusColor(analysis?.status_women_children)}`}>
                        {analysis?.status_women_children === 'within_limit' ? '✓ Within Limit' : 
                         analysis?.status_women_children === 'borderline' ? '⚠ Borderline' : '✗ Exceeds Limit'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sugar Type Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Added vs Natural */}
                  <div className="bg-white rounded-2xl shadow-lg p-6">
                    <h3 className="font-bold mb-4">Sugar Type Breakdown</h3>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={getSugarTypeData()}
                            cx="50%"
                            cy="50%"
                            outerRadius={70}
                            dataKey="value"
                            label={({ name, value }) => `${name}: ${value.toFixed(1)}g`}
                          >
                            <Cell fill="#ef4444" />
                            <Cell fill="#22c55e" />
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Sugar Sources */}
                  <div className="bg-white rounded-2xl shadow-lg p-6">
                    <h3 className="font-bold mb-4">Sugar Sources (%)</h3>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={getSugarSourcesData()} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" domain={[0, 100]} />
                          <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Bar dataKey="value" fill="#a855f7" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* AHA Disclaimer */}
                <div className="bg-purple-50 border border-purple-200 rounded-2xl p-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-purple-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-purple-800 mb-2">American Heart Association Guidelines</h3>
                      <ul className="text-sm text-purple-700 space-y-1">
                        <li><strong>Men:</strong> Should consume less than <strong>36 grams</strong> (9 teaspoons) of added sugar per day</li>
                        <li><strong>Women and Children:</strong> Should consume less than <strong>25 grams</strong> (6 teaspoons) of added sugar per day</li>
                      </ul>
                      <p className="text-xs text-purple-600 mt-2">
                        Note: These limits refer to <em>added sugars</em>, not naturally occurring sugars found in fruits and dairy.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Educational Content */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-start gap-3">
                    <Info className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-gray-800 mb-3">Understanding Sugar and Your Health</h3>
                      <div className="text-sm text-gray-600 space-y-3">
                        <p>
                          <strong>Added Sugars</strong> are sugars added to foods during processing or preparation. 
                          They include white sugar, brown sugar, high-fructose corn syrup, honey, and maple syrup when 
                          added to foods. Excessive added sugar consumption is linked to obesity, type 2 diabetes, 
                          heart disease, and tooth decay.
                        </p>
                        <p>
                          <strong>Natural Sugars</strong> are found naturally in whole foods like fruits (fructose) 
                          and dairy products (lactose). These come packaged with fiber, vitamins, and minerals that 
                          slow absorption and provide nutritional benefits. They are generally not a concern when 
                          consumed as part of whole foods.
                        </p>
                        <p>
                          <strong>Hidden Sugar Sources</strong> - Many processed foods contain hidden sugars. Common 
                          culprits include: sweetened beverages (sodas, juices, energy drinks), breakfast cereals, 
                          flavored yogurts, sauces (ketchup, BBQ sauce), bread, and "low-fat" products that compensate 
                          with added sugar.
                        </p>
                        <p className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                          <strong>Blood Sugar Impact:</strong> Added sugars cause rapid spikes in blood glucose, 
                          triggering insulin release. Over time, this can lead to insulin resistance, a precursor 
                          to type 2 diabetes. Maintaining stable blood sugar through reduced added sugar intake 
                          supports metabolic health and sustained energy levels.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Overall Assessment */}
                {analysis?.overall_assessment && (
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl p-6 text-white">
                    <h3 className="font-bold mb-2">Overall Assessment</h3>
                    <p>{analysis.overall_assessment}</p>
                  </div>
                )}
              </motion.div>
            )}

            {/* Culprit Foods Tab */}
            {activeTab === 'culprit-foods' && (
              <motion.div
                key="culprit-foods"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                      <ThumbsDown className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">My Culprit Foods</h2>
                      <p className="text-sm text-muted-foreground">
                        Highest sugar foods from the past week
                      </p>
                    </div>
                  </div>

                  {analysis?.culprit_foods?.length > 0 ? (
                    <div className="space-y-4">
                      {analysis.culprit_foods.map((food, index) => (
                        <div 
                          key={`culprit-${food.name}-${index}`}
                          className="flex items-start gap-4 p-4 bg-red-50 rounded-xl border border-red-200"
                        >
                          <div className="w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center font-bold flex-shrink-0">
                            {food.rank || index + 1}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold">{food.name}</h3>
                              <span className="text-xs bg-red-200 text-red-700 px-2 py-0.5 rounded-full">
                                {food.meal_type}
                              </span>
                              <span className="text-xs text-muted-foreground">{food.date}</span>
                            </div>
                            <div className="flex flex-wrap gap-3 text-sm mb-2">
                              <span className="text-purple-600">
                                <strong>Total Sugar:</strong> {food.total_sugar_g}g
                              </span>
                              <span className="text-red-600">
                                <strong>Added Sugar:</strong> {food.added_sugar_g}g
                              </span>
                              <span className={food.sugar_type === 'added' ? 'text-red-500' : 'text-green-500'}>
                                <strong>Type:</strong> {food.sugar_type}
                              </span>
                            </div>
                            <p className="text-sm text-red-700">{food.reason}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <ThumbsUp className="w-12 h-12 mx-auto mb-2 text-emerald-300" />
                      <p>Great job! No high-sugar foods identified this week.</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Good Foods Tab */}
            {activeTab === 'good-foods' && (
              <motion.div
                key="good-foods"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <ThumbsUp className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">My Good Foods</h2>
                      <p className="text-sm text-muted-foreground">
                        Lowest sugar foods from the past week
                      </p>
                    </div>
                  </div>

                  {analysis?.good_foods?.length > 0 ? (
                    <div className="space-y-4">
                      {analysis.good_foods.map((food, index) => (
                        <div 
                          key={`good-${food.name}-${index}`}
                          className="flex items-start gap-4 p-4 bg-emerald-50 rounded-xl border border-emerald-200"
                        >
                          <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold flex-shrink-0">
                            {food.rank || index + 1}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold">{food.name}</h3>
                              <span className="text-xs bg-emerald-200 text-emerald-700 px-2 py-0.5 rounded-full">
                                {food.meal_type}
                              </span>
                              <span className="text-xs text-muted-foreground">{food.date}</span>
                            </div>
                            <div className="flex flex-wrap gap-3 text-sm mb-2">
                              <span className="text-purple-600">
                                <strong>Total Sugar:</strong> {food.total_sugar_g}g
                              </span>
                              <span className="text-emerald-600">
                                <strong>Added Sugar:</strong> {food.added_sugar_g}g
                              </span>
                              <span className={food.sugar_type === 'natural' ? 'text-green-500' : 'text-amber-500'}>
                                <strong>Type:</strong> {food.sugar_type}
                              </span>
                            </div>
                            <p className="text-sm text-emerald-700">{food.reason}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <AlertTriangle className="w-12 h-12 mx-auto mb-2 text-amber-300" />
                      <p>No low-sugar foods identified. Try adding more whole foods to your diet!</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </main>
    </div>
  );
};

export default SugarHomeostasisProgramPage;
